/**
 * Embedded Wallet & USDC Contract Configuration
 *
 * Base Sepolia (testnet): Chain ID 84532
 * Base Mainnet: Chain ID 8453
 */

import { baseSepolia, base } from 'wagmi/chains';
import { WALLET_ENABLED } from './features';
import { IS_TESTNET_BUILD } from './chain';

// Determine if we're on testnet or mainnet (one rule, in ./chain, which the
// wallet API service also declares to the server on every money request)
export const isTestnet = IS_TESTNET_BUILD;

// USDC Contract Addresses
export const USDC_ADDRESSES = {
  baseSepolia: '0x036CbD53842c5426634e7929541eC2318f3dCF7e' as `0x${string}`,
  base: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913' as `0x${string}`,
} as const;

// Get the correct USDC address based on environment
export const USDC_ADDRESS = isTestnet ? USDC_ADDRESSES.baseSepolia : USDC_ADDRESSES.base;

// Protocol wallet for collecting fees
// CRITICAL: This must be set in production - funds sent here cannot be recovered if wrong.
// Only enforced when the wallet/tipping feature is actually enabled. import.meta.env.PROD
// is true for ANY `vite build` (including the wallet-DISABLED mobile-staging /
// mobile-production builds, which never set VITE_PROTOCOL_WALLET), so without the
// WALLET_ENABLED guard this module-init throw white-screens the whole app on those builds.
const _protocolWallet = import.meta.env.VITE_PROTOCOL_WALLET;
if (!_protocolWallet && import.meta.env.PROD && WALLET_ENABLED) {
  throw new Error(
    'CRITICAL: VITE_PROTOCOL_WALLET environment variable is not set in production (wallet enabled)!',
  );
}
if (_protocolWallet && !/^0x[a-fA-F0-9]{40}$/.test(_protocolWallet)) {
  throw new Error(`Invalid VITE_PROTOCOL_WALLET format: ${_protocolWallet}`);
}
// In development, allow missing protocol wallet with warning; in production, fail fast above
export const PROTOCOL_WALLET = _protocolWallet as `0x${string}` | undefined;
export const PROTOCOL_WALLET_CONFIGURED = !!_protocolWallet;

// Chain configuration
export const CHAIN = isTestnet ? baseSepolia : base;
export const CHAIN_ID = isTestnet ? baseSepolia.id : base.id;

// USDC has 6 decimals (not 18 like ETH)
export const USDC_DECIMALS = 6;

// Protocol fee in USDC (0.01 = 10000 in 6 decimals)
// TEMPORARY: Reduced for testing with limited testnet USDC
export const PROTOCOL_FEE = 0.01;
export const PROTOCOL_FEE_RAW = BigInt(10000); // 0.01 * 10^6

// Minimum amounts
// TEMPORARY: Reduced for testing with limited testnet USDC
export const MIN_TIP_AMOUNT = 0.02; // $0.02 minimum tip (total with fee: $0.03)
export const MIN_WITHDRAWAL_AMOUNT = 0.02; // $0.02 minimum withdrawal

// ERC-20 ABI for USDC transfers (minimal ABI for what we need)
export const ERC20_ABI = [
  {
    name: 'transfer',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'to', type: 'address' },
      { name: 'amount', type: 'uint256' },
    ],
    outputs: [{ name: '', type: 'bool' }],
  },
  {
    name: 'balanceOf',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'account', type: 'address' }],
    outputs: [{ name: '', type: 'uint256' }],
  },
  {
    name: 'decimals',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint8' }],
  },
  {
    name: 'symbol',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'string' }],
  },
  {
    name: 'approve',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'spender', type: 'address' },
      { name: 'amount', type: 'uint256' },
    ],
    outputs: [{ name: '', type: 'bool' }],
  },
  {
    name: 'allowance',
    type: 'function',
    stateMutability: 'view',
    inputs: [
      { name: 'owner', type: 'address' },
      { name: 'spender', type: 'address' },
    ],
    outputs: [{ name: '', type: 'uint256' }],
  },
] as const;

// Helper to convert human-readable USDC to raw amount (6 decimals)
export function parseUSDC(amount: number | string): bigint {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  return BigInt(Math.round(num * 10 ** USDC_DECIMALS));
}

// Helper to convert raw USDC amount to human-readable (6 decimals)
export function formatUSDC(rawAmount: bigint): string {
  const num = Number(rawAmount) / 10 ** USDC_DECIMALS;
  return num.toFixed(2);
}

// Helper to format USDC with dollar sign
export function formatUSDCDisplay(rawAmount: bigint): string {
  return `$${formatUSDC(rawAmount)}`;
}

// Block explorer URL helpers
const EXPLORER_BASE_URL = CHAIN.blockExplorers?.default?.url || 'https://basescan.org';
export const EXPLORER_NAME = CHAIN.blockExplorers?.default?.name || 'BaseScan';

/**
 * Get block explorer URL for a transaction hash
 */
export function getExplorerTxUrl(txHash: string): string {
  return `${EXPLORER_BASE_URL}/tx/${txHash}`;
}

/**
 * Get block explorer URL for an address
 */
export function getExplorerAddressUrl(address: string): string {
  return `${EXPLORER_BASE_URL}/address/${address}`;
}
